import { useEffect, useMemo, useState } from 'react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import {
  ExceptionScopeTypeLocation,
  ExceptionScopeTypeResource,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/exception.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getTodaysDate } from '@frontend/date';
import {
  useCalendarEventsConfigurationShallowStore,
  useCalendarEventsFormTypeShallowStore,
} from '@frontend/schedule-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { OnlineSchedulingAppointmentPromoCard } from '@frontend/self-upgrade';
import { Tray, useModalControl } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../../../constants';
import { AddEditRuleExceptionsTray } from '../../../availability/rule-exceptions/components/add-edit-rule-exceptions-tray';
import { CalendarEventSelector } from '../../components/CalendarEventsSelector';
import { CalendarEventsEnums } from '../../types';
import { isUUID } from '../../utils';
import { DEFAULT_CONFIGURED_CALENDAR_EVENT_VALUE } from '../constant';
import { CalendarEventsModalContent } from './CalendarEventsModalContent';

export const CalendarEventsContainer = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const { configuredCalendarEvent, setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore(
    'configuredCalendarEvent',
    'setConfiguredCalendarEvent'
  );

  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM();
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  const { selectedForm, setSelectedForm, setSelectedPerson } = useCalendarEventsFormTypeShallowStore(
    'selectedForm',
    'setSelectedForm',
    'setSelectedPerson'
  );

  const calendarEventSelectionModalProps = useModalControl();
  const eventPanelModalProps = useModalControl({ disableReturnFocus: true });
  const { modalProps, openModal, closeModal } = eventPanelModalProps;
  const {
    modalProps: ruleExceptionModalProps,
    openModal: openRuleExceptionModal,
    closeModal: closeRuleExceptionModal,
  } = useModalControl();

  const ruleExceptionLocationId = configuredCalendarEvent?.locationId || selectedLocationIds[0] || '';
  const ruleException = useMemo(() => {
    const startDate = configuredCalendarEvent?.calendarDateValue || getTodaysDate(DATETIME_FORMATS.ISO_DATE);

    const commonException = {
      nextOccurrenceStart: startDate,
      endDate: startDate,
      time: {
        startTime: configuredCalendarEvent?.startHour || '',
        endTime: configuredCalendarEvent?.endHour || '',
      },
    };

    // Location Scope
    if (!configuredCalendarEvent?.providerId) {
      return {
        ...commonException,
        target: { scopeType: ExceptionScopeTypeLocation },
      };
    }

    // Provider Scope
    return {
      ...commonException,
      target: {
        scopeType: ExceptionScopeTypeResource,
        logicOperator: 'OR',
        resources: [
          {
            type: 'PROVIDER',
            id: configuredCalendarEvent?.providerId || '',
            name: configuredCalendarEvent?.providerName || '',
          },
        ],
      },
    };
  }, [configuredCalendarEvent]);

  const [locationIdsForOnlineSchedulePromo, setLocationIdsForOnlineSchedulePromo] = useState<string[]>([]);

  // useEffect to manage the modal or popover triggers
  useEffect(() => {
    const hasProviderDetails = configuredCalendarEvent?.providerId || configuredCalendarEvent?.providerName;

    const isValidEvent = configuredCalendarEvent?.isValid;

    const calendarEventType = configuredCalendarEvent?.calendarEventType ?? '';

    if (calendarEventType === CalendarEventsEnums.APPOINTMENT_EVENT) {
      setSelectedForm(calendarEventType);
      openModal();
      return;
    }

    const isOutOfOfficeEvent =
      calendarEventType === CalendarEventsEnums.OFFICE_HOURS_OUT_OF_OFFICE_EVENT ||
      calendarEventType === CalendarEventsEnums.PROVIDER_OUT_OF_OFFICE_EVENT;

    if (isValidEvent && !hasProviderDetails) {
      setSelectedForm(CalendarEventsEnums.OFFICE_HOURS_OUT_OF_OFFICE_EVENT);
      if (isScheduleV3FlagEnabled) {
        openRuleExceptionModal();
      } else {
        openModal();
      }
    } else if (isValidEvent && calendarEventType && isOutOfOfficeEvent) {
      setSelectedForm(calendarEventType);
      if (isScheduleV3FlagEnabled) {
        openRuleExceptionModal();
      } else {
        openModal();
      }
    } else if (isValidEvent && calendarEventType) {
      setSelectedForm(calendarEventType);
      openModal();
    } else if (configuredCalendarEvent && configuredCalendarEvent.isValid) {
      setSelectedForm('');
      calendarEventSelectionModalProps.openModal();
    }
  }, [configuredCalendarEvent]);

  const isOutOfOfficeEventForm =
    selectedForm === CalendarEventsEnums.OFFICE_HOURS_OUT_OF_OFFICE_EVENT ||
    selectedForm === CalendarEventsEnums.PROVIDER_OUT_OF_OFFICE_EVENT;

  useEffect(() => {
    if (selectedForm) {
      if (isScheduleV3FlagEnabled && isOutOfOfficeEventForm) {
        openRuleExceptionModal();
      } else {
        openModal();
      }
    }
  }, [isOutOfOfficeEventForm, isScheduleV3FlagEnabled, openModal, openRuleExceptionModal, selectedForm]);

  useEffect(() => {
    if (selectedLocationIds) {
      handleEventsSelectionCancelButtonClick();
      closeModal();
    }
  }, [selectedLocationIds]);

  const onCalendarEventSelect = (event: CalendarEventsEnums) => {
    if (event) {
      setSelectedForm(event);
    }
  };

  const handleModalClose = () => {
    setSelectedForm('');
    setSelectedPerson(null);
    closeModal();
    setConfiguredCalendarEvent(DEFAULT_CONFIGURED_CALENDAR_EVENT_VALUE);
  };

  const handleCloseRuleExceptionTray = () => {
    setSelectedForm('');
    setSelectedPerson(null);
    closeRuleExceptionModal();
    setConfiguredCalendarEvent(DEFAULT_CONFIGURED_CALENDAR_EVENT_VALUE);
  };

  const handleEventsSelectionCancelButtonClick = () => {
    setSelectedForm('');
    calendarEventSelectionModalProps.closeModal();
    setConfiguredCalendarEvent(DEFAULT_CONFIGURED_CALENDAR_EVENT_VALUE);
  };

  const handleAppointmentSuccess = (appointment?: Appointment) => {
    // Added 2s delay to avoid conflicts with the toast message
    setTimeout(() => {
      appointment?.clientLocationIds?.length && setLocationIdsForOnlineSchedulePromo(appointment.clientLocationIds);
    }, 2000);
  };

  const isValidProviderId = configuredCalendarEvent?.providerId && isUUID(configuredCalendarEvent.providerId);

  const shouldShowCalendarEventsPopover =
    hasWSM && ((!configuredCalendarEvent?.providerId && !configuredCalendarEvent?.locationId) || isValidProviderId);

  return (
    <>
      {shouldShowCalendarEventsPopover && (
        <CalendarEventSelector
          modalProps={calendarEventSelectionModalProps.modalProps}
          onCalendarEventSelect={onCalendarEventSelect}
          onModalClose={handleEventsSelectionCancelButtonClick}
        />
      )}

      <Tray
        width='medium'
        autoFocusTimeout={3600000 * 2}
        {...modalProps}
        onClose={handleModalClose}
        style={selectedForm === CalendarEventsEnums.APPOINTMENT_EVENT ? { padding: 0, gap: 0 } : undefined}
      >
        <CalendarEventsModalContent closeModal={handleModalClose} onAppointmentSuccess={handleAppointmentSuccess} />
      </Tray>

      {ruleExceptionModalProps.show && (
        <AddEditRuleExceptionsTray
          locationId={ruleExceptionLocationId}
          selectedException={ruleException}
          modalProps={ruleExceptionModalProps}
          onClose={handleCloseRuleExceptionTray}
        />
      )}

      {!modalProps.show && !ruleExceptionModalProps.show && (
        <OnlineSchedulingAppointmentPromoCard
          locationIds={locationIdsForOnlineSchedulePromo}
          onClose={() => setLocationIdsForOnlineSchedulePromo([])}
        />
      )}
    </>
  );
};
